import { AxiosResponse } from "axios";
import {
  GetAllActiveBinsListApiResponse,
  GetAllBinsListApiResponse,
  GetPartBinListApiResponse,
} from "../../@types/DRO/GetAllBinsResponse";
import { GeneralResponseForAllApi } from "../../@types/DRO/PurchaseOrderResponse";
import { erpAxios, getAuthToken } from "../axiosInstances";

function getResponseHeader(
  headers: AxiosResponse["headers"],
  name: string
): string | undefined {
  const value = headers[name];
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value[0];
  return undefined;
}

export interface CreatePartBinPayload {
  partId: number;
  warehouse: number;
  binId: number;
  quantity: number;
}

export interface MovePartsPayload {
  partId: number;
  sourceBinId: number;
  destinationBinId: number;
  quantity: number;
}

export interface AddBinPayload {
  name: string;
  warehouseId: number;
  active: boolean;
}

export interface UpdateBinPayload {
  name: string;
  active: boolean;
  transferBinId: number | null;
}

export class WarehouseBinsApi {
  public static api = new WarehouseBinsApi();
  private instance = erpAxios;

  getToken() {
    return getAuthToken();
  }

  private downloadFile(response: AxiosResponse<Blob>, defaultFileName: string) {
    const contentType = getResponseHeader(response.headers, "content-type");
    const blob = new Blob([response.data], {
      type: contentType || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const contentDisposition = getResponseHeader(response.headers, "content-disposition");
    let filename = `${defaultFileName}-${new Date().toISOString().split("T")[0]}.xlsx`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  async getAllBinsList(
    page: number = 0,
    size: number = 5,
    search: string = "",
    status?: string
  ): Promise<GetAllBinsListApiResponse> {
    try {
      const response: AxiosResponse<GetAllBinsListApiResponse> =
        await this.instance.get("erp/api/v1/bins/get/all/bins", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            search,
            page,
            size,
            ...(status !== undefined && { status }),
          },
        });

      return response.data;
    } catch (error) {
      console.error("Error fetching bins data", error);
      throw error;
    }
  }

  async getAllActiveBinsList(
    page: number = 0,
    size: number = 5,
    search?: string
  ): Promise<GetAllActiveBinsListApiResponse> {
    try {
      const response: AxiosResponse<GetAllActiveBinsListApiResponse> =
        await this.instance.get("erp/api/v1/bins/get/all/active/bins", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            page,
            size,
            ...(search ? { search } : {}),
          },
        });

      return response.data;
    } catch (error) {
      console.error("Error fetching active bins data", error);
      throw error;
    }
  }

  async getPartBinList(
    page: number = 0,
    size: number = 5,
    search: string = "",
    status?: string
  ): Promise<GetPartBinListApiResponse> {
    try {
      const response: AxiosResponse<GetPartBinListApiResponse> =
        await this.instance.get("erp/api/v1/bins/get/part/bin", {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          params: {
            search,
            page,
            size,
            ...(status !== undefined && { status }),
          },
        });

      return response.data;
    } catch (error) {
      console.error("Error fetching part bin data", error);
      throw error;
    }
  }

  async getExpandedBinParts(binId: number): Promise<any> {
    try {
      const response = await this.instance.get(`erp/api/v1/bins/expand/part/bin/${binId}`, {
        headers: {
          Authorization: `Bearer ${this.getToken()}`,
        },
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching expanded bin parts", error);
      throw error;
    }
  }

  async getBinsSampleSheet(): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/api/v1/bins/sample/sheet",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          responseType: "blob",
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching bins sample sheet", error);
      throw error;
    }
  }

  async getBinsSampleSheetCreateBin(): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/api/v1/bins/sample/sheet/create/bin",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          responseType: "blob",
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching bins sample sheet (create bin)", error);
      throw error;
    }
  }

  async getBinsBulkUploadCreateBin(): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/api/v1/bins/bulk/upload/create/bin",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          responseType: "blob",
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching bins bulk upload create bin", error);
      throw error;
    }
  }

  async downloadWarehouseBins(): Promise<Blob> {
    try {
      const response: AxiosResponse<Blob> = await this.instance.get(
        "erp/api/v1/bins/download/warehouse-bins",
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
          responseType: "blob",
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error downloading warehouse bins", error);
      throw error;
    }
  }

  async bulkUploadBins(file: File): Promise<{ success: boolean; message?: string; hasErrorFile?: boolean }> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await this.instance.post(
        "erp/api/v1/bins/bulk/upload",
        formData,
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        }
      );

      const contentType = getResponseHeader(response.headers, "content-type");

      if (
        contentType?.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
        contentType?.includes("application/vnd.ms-excel") ||
        contentType?.includes("application/octet-stream")
      ) {
        this.downloadFile(response, "bulk-upload-errors");
        return {
          success: false,
          message: "Some records could not be processed. Please check the downloaded error file for details.",
          hasErrorFile: true,
        };
      }

      const text = await response.data.text();

      try {
        const jsonData = JSON.parse(text);
        return {
          success: true,
          message: jsonData?.message || jsonData?.data || "Bins uploaded successfully!",
        };
      } catch {
        return {
          success: true,
          message: "Bins uploaded successfully!",
        };
      }
    } catch (error) {
      console.error("Error bulk uploading bins", error);
      throw error;
    }
  }

  async uploadBulkBins(file: File): Promise<{ success: boolean; message?: string; hasErrorFile?: boolean }> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await this.instance.post(
        "erp/api/v1/bins/bulk/upload/create/bin",
        formData,
        {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
            "Content-Type": "multipart/form-data",
          },
          responseType: "blob",
        }
      );

      const contentType = getResponseHeader(response.headers, "content-type");

      if (
        contentType?.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") ||
        contentType?.includes("application/vnd.ms-excel") ||
        contentType?.includes("application/octet-stream")
      ) {
        this.downloadFile(response, "bulk-upload-errors");
        return {
          success: false,
          message: "Some records could not be processed. Please check the downloaded error file for details.",
          hasErrorFile: true,
        };
      }

      const text = await response.data.text();

      try {
        const jsonData = JSON.parse(text);
        return {
          success: true,
          message: jsonData?.message || jsonData?.data || "Bins uploaded successfully!",
        };
      } catch {
        return {
          success: true,
          message: "Bins uploaded successfully!",
        };
      }
    } catch (error) {
      console.error("Error uploading bins file", error);
      throw error;
    }
  }

  async createPartBin(
    payload: CreatePartBinPayload
  ): Promise<GeneralResponseForAllApi> {
    try {
      const response: AxiosResponse<GeneralResponseForAllApi> =
        await this.instance.post("erp/api/v1/bins/create/part/bin", payload, {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        });

      return response.data;
    } catch (error) {
      console.error("Error creating part bin", error);
      throw error;
    }
  }

  async moveParts(payload: MovePartsPayload): Promise<GeneralResponseForAllApi> {
    try {
      const response: AxiosResponse<GeneralResponseForAllApi> =
        await this.instance.post("erp/api/v1/bins/move/parts", payload, {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        });

      return response.data;
    } catch (error) {
      console.error("Error moving parts", error);
      throw error;
    }
  }

  async addBin(payload: AddBinPayload): Promise<GeneralResponseForAllApi> {
    try {
      const response: AxiosResponse<GeneralResponseForAllApi> =
        await this.instance.post("erp/api/v1/bins/add", payload, {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        });

      return response.data;
    } catch (error) {
      console.error("Error adding bin", error);
      throw error;
    }
  }

  async updateBin(
    id: number,
    payload: UpdateBinPayload
  ): Promise<GeneralResponseForAllApi> {
    try {
      const response: AxiosResponse<GeneralResponseForAllApi> =
        await this.instance.put(`erp/api/v1/bins/update/${id}`, payload, {
          headers: {
            Authorization: `Bearer ${this.getToken()}`,
          },
        });

      return response.data;
    } catch (error) {
      console.error("Error updating bin", error);
      throw error;
    }
  }
}